import { plural } from '../speak';

const columns = (n: number) => plural('en', n, { one: '# column', other: '# columns' });
const andList = (words: readonly string[]) => (words.length <= 2 ? words.join(' and ') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`);

/**
 * The panel on the right: its head, its tabs, its search, and the settings of
 * what is picked. A setting is known by its name in English (its
 * `data-setting`, which the panel and its search keep); `settingName` says it
 * in the designer's language.
 */
export const panel = {
  properties: 'Properties',
  settings: 'Settings',
  tabs: { content: 'Content', layout: 'Layout', look: 'Look', rules: 'Rules', data: 'Data' },
  /** A setting's name, by the English name the panel knows it by. */
  settingName: (key: string) => key,
  /** The page's own setting, named apart from the part's of the same name. */
  pagesSetting: (name: string) => `The page’s ${name.toLowerCase()}`,
  pagesLook: 'the page’s look',
  // ---- search
  searchSettings: 'Search settings',
  settingsFound: 'Settings found',
  noSetting: (query: string) => `No setting called “${query}”.`,
  // ---- the head: what is picked
  heads: {
    several: 'Several',
    field: 'Field',
    group: 'Group',
    sideBySide: 'Side by side',
    tabs: 'Tabs',
    tab: 'Tab',
    heading: 'Heading',
    words: 'Words',
    part: 'Part',
    button: 'Button',
    image: 'Image',
    map: 'Map',
    related: 'Related list',
    divider: 'Divider',
    spacer: 'Spacer',
    slot: 'The app’s own part',
    form: 'Saved form',
    counter: 'Counter',
    badge: 'Badge',
    ribbon: 'Ribbon',
    alert: 'Alert',
    menuItem: 'Gear menu item',
    statusSteps: 'Status steps',
    list: 'List',
    column: 'Column',
    sheet: 'Sheet',
    screen: 'Screen',
  },
  partsPicked: (n: number) => `${n} parts picked`,
  fromTheModel: 'from the model',
  untitledList: 'Untitled list',
  // ---- a field
  label: 'Label',
  helpText: 'Help text',
  optional: 'Optional',
  placeholder: 'Placeholder',
  placeholderHint: 'Words inside the empty box',
  /** The field that takes the focus as the record opens: Flectra's default_focus. */
  focusOnOpen: 'Focused as the record opens',
  focusOnOpenHint: 'The cursor starts here, unless the person is busy elsewhere on the screen.',
  /** Other words in the empty box while a condition holds, the first that holds. */
  placeholderWhen: 'Other words while',
  placeholderWhenAdd: 'Add words for a condition',
  placeholderWhenWords: 'Words',
  placeholderWhenCondition: 'While',
  placeholderWhenRemove: (n: number) => `Remove words ${n}`,
  placeholderWhenNeedsWords: 'Words in the empty box need words',
  shownAs: 'Shown as',
  options: 'Options',
  duplicate: 'Duplicate',
  deleteField: 'Delete field',
  section: 'Section',
  required: 'Required',
  requiredOnlyWhen: 'Required only when…',
  onlyWhen: 'Only when…',
  readonlyWhen: 'Read-only when…',
  readonlyReason: 'Why',
  readonlyReasonHint: 'Set by the workflow when it is resolved',
  showOnlyWhen: 'Show only when…',
  noRulesField: 'Always. A rule needs another field that holds one of a list, or yes or no.',
  whenItShows: 'When it shows',
  fieldName: 'Field name',
  storedAs: 'Stored as',
  keepsName: 'From your model: it keeps its name.',
  storedUnder: 'What the answer is stored under.',
  fromYourModel: (stored: string) => `${stored} · from your model`,
  optionsFromModel: 'Its options come from the model.',
  currencyFromModel: 'Its currency comes from the model.',
  recordsFromModel: 'The records it points to come from the model.',
  // ---- a group, tabs, a tab
  sectionTitle: 'Section title',
  moveUp: 'Move up',
  moveDown: 'Move down',
  moveLeft: 'Move left',
  moveRight: 'Move right',
  deleteSection: 'Delete section',
  noRulesGroup: 'Always. A rule needs a field outside it that holds one of a list, or yes or no.',
  title: 'Title',
  deleteTabs: 'Delete tabs',
  tabsHint: 'Tabs show one page of sections at a time. Select a tab to rename it.',
  tabLabel: 'Tab label',
  deleteTab: 'Delete tab',
  folds: 'Folds',
  no: 'No',
  yes: 'Yes',
  startsOpen: 'Starts open',
  startsOpenTitle: 'People can fold it by its title',
  startsFolded: 'Starts folded',
  startsFoldedTitle: 'It starts folded: people open it by its title',
  foldsWhy: 'A group folds by its title. Give it a title to let it fold.',
  // ---- the page
  description: 'Description',
  wordsUnderTitle: 'Words under the title',
  layout: 'Layout',
  sections: 'Sections',
  recordSheet: 'Record sheet',
  titleField: 'Title field',
  titleFieldHint: 'The text field the record is named by, in big letters at the top.',
  titleLabel: 'Words over the title',
  titleLabelHint: 'Small words over the title, as “Product name” over a product’s name.',
  nothingPicked: 'Nothing is picked, so these are the screen’s own settings. Pick a field or a section to change it.',
  layoutHint: 'A record sheet names its record at the top, and can have tabs, a status bar and buttons.',
  // ---- around a record: its gear menu's items, the pager and trail over it, the attachment and the side panel beside it
  builtin: 'The record’s own',
  builtins: { none: 'None: its steps or the app’s action', archive: 'Archive', unarchive: 'Unarchive', duplicate: 'Duplicate', delete: 'Delete', print: 'Print the record' },
  builtinHint: 'Archive, Unarchive, Duplicate and Delete work through the app’s data source, asking first before archiving or deleting; Print prints the record as the page prints. Steps of its own run instead — an Archive that opens a wizard.',
  menuGroup: 'Under',
  menuGroups: { actions: 'Actions', print: 'Print' },
  /** An item that opens a menu of its own: that menu's items, and the app's action that gives more as it opens. */
  itsMenu: 'Its menu',
  itsMenuHint: 'The items it opens beside it, each with its words and the app’s action. It does nothing itself.',
  itemWords: 'Item’s words',
  itemAction: 'Its action',
  addItem: 'Add an item',
  menuFrom: 'Items the app gives as it opens',
  menuFromHint: 'The name of the app’s action asked for more items as the menu opens, such as templates that depend on the record. They come after its own.',
  pagerShown: 'Pager over the record',
  breadcrumbsShown: 'Breadcrumbs over the record',
  aroundHint: 'They show when the app gives the records round this one and the trail to it — never in a dialog.',
  attachmentBeside: 'Attachment beside the sheet',
  // ---- the page's time zone
  pageTimeZone: 'Time zone',
  browserZone: 'The browser’s own',
  pageTimeZoneHint: 'Every date and time on the page is shown and typed on this zone’s clock — a branch’s, a clinic’s — and today is its day there.',
  // ---- on paper
  printHeader: 'Printout’s header',
  printHeaderHint: 'Words at the top of every printout of the page, beside the logo: your company’s name and address. {name} shows a field’s value.',
  printFooter: 'Printout’s footer',
  printFooterHint: 'Words at its foot: terms, a bank account.',
  printLogo: 'Logo’s address',
  printLogoHint: 'Your logo, by its address (https://…, or a path on your app): at the top of the printout.',
  attachmentNone: 'None',
  attachmentFromApp: 'The record’s attachments, from the app',
  attachmentHint: 'A PDF or a picture beside the sheet on a wide screen, under it on a narrow one.',
  sideBeside: 'Side panel beside the sheet',
  sideBesides: { wide: 'On a wide form', always: 'Always, but on a phone' },
  noTitle: 'No title',
  // ---- the page's phone numbers
  phones: 'Phone numbers',
  phonesAs: { typed: 'As typed', checked: 'Checked by their country' },
  phonesHint: 'Checked by their country: a phone field has its country picked or typed with +, refuses a number its country has none like, keeps it as +201012345678, and shows it with a call and a WhatsApp link.',
  phoneCountry: 'Phone country to start on',
  noPhoneCountry: 'None: written with + and the country code',
  phoneShown: 'Phone numbers shown',
  phoneShownAs: { international: 'Abroad’s way: +20 10 1234 5678', national: 'At home’s way: 010 1234 5678' },
  phoneCountries: 'Phone countries picked from',
  phoneCountriesAs: { list: 'Fieldia’s list, found by typing', native: 'The browser’s own list' },
  // ---- columns, rows, width, labels
  screens: { wide: 'Desktop', medium: 'Tablet', narrow: 'Phone' },
  columnsOn: { wide: 'Columns on a desktop', medium: 'Columns on a tablet', narrow: 'Columns on a phone' },
  columnsInside: 'Columns inside',
  columns: 'Columns',
  auto: 'Auto',
  autoTitle: 'As the skin stacks them on a smaller screen',
  twelfths: 'Twelfths',
  twelfthsTitle: 'Each row divided its own way, in twelfths',
  columnCount: (n: number) => columns(n),
  onTracks: (n: number, around: string) => `Its parts sit on the ${n} columns it covers in “${around}”, so they line up with everything above and below. Make it wider or narrower to change that.`,
  sharedColumn: (n: number, around: string) => `Its ${n} parts share one column of “${around}”, side by side. Where that column is narrow, they go one under the other.`,
  twelfthsHint: 'Each row divides its own way, in twelfths: drop a part beside another to share its row. Auto keeps a row’s proportions on a tablet, and puts its parts one under another on a phone.',
  columnsHint: 'Each size of screen can have its own. Auto stacks them as the skin does: one column on a phone.',
  rows: 'Rows',
  keepRowsFull: 'Keep each row full',
  allowGaps: 'Allow gaps',
  rowsFullHint: 'A part leaving a row widens the rest to fill it; a part alone takes the whole row.',
  gapsHint: 'A part keeps its width when a neighbour leaves, and its far edge can be pulled in to leave room.',
  width: 'Width',
  all: (n: number) => `All ${n}`,
  allColumns: (n: number) => `All ${n} columns`,
  /** The parts sharing a row, named: one or two, else how many. */
  others: (names: readonly string[], count: number) => (count <= 2 ? andList(names) : `${count} others`),
  sharesWith: (span: number, cols: number, others: string) => `${span} of the ${cols} columns it shares with ${others}.`,
  ofColumnsOf: (span: number, cols: number, group: string) => `${span} of the ${cols} columns of “${group}”.`,
  oneColumnFull: (group: string) => `“${group}” has one column, so it takes the full width.`,
  fullWidthOn: (inTab: boolean) => `It sits ${inTab ? 'in a tab' : 'on the page'}, so it takes the full width. Put it in a group with columns, or side by side with another part, to give it a width.`,
  percent: (n: number) => `${n}%`,
  percentOfRow: (n: number) => `${n}% of the row`,
  aloneFills: (group: string) => `The whole row in ${group}: alone in its row, it fills it. Let the group’s rows leave gaps to make it narrower.`,
  shareIn: (share: string, group: string, full: boolean) =>
    `${share.charAt(0).toUpperCase() + share.slice(1)} in ${group}. ${full ? 'Its row stays full: the rest of it takes up the difference.' : 'Narrower, it leaves room in its row.'}`,
  labels: 'Labels',
  labelsInside: 'Labels inside',
  asGroup: 'As group',
  asPage: 'As page',
  above: 'Above',
  aboveTitle: 'Above their boxes',
  beside: 'Beside',
  besideTitle: 'Beside their boxes',
  inBox: 'In box',
  inBoxTitle: 'Inside the box, as its placeholder; still read out by screen readers',
  labelInBox: 'In box: the label is the empty box’s placeholder, still read out by screen readers.',
  labelWidth: 'Label width',
  px: 'px',
  // ---- the look
  accentColour: 'Accent colour',
  swatches: { '#1677ff': 'Blue', '#002855': 'Navy', '#1f7a4d': 'Green', '#6941c6': 'Purple', '#c4320a': 'Orange', '#0e7c86': 'Teal' } as Record<string, string>,
  anyAccent: 'Any accent colour',
  anyColour: 'Any colour',
  accentAsSkin: 'Accent as the skin has it',
  asTheSkin: 'As the skin',
  accentHint: 'Buttons, the tab and the box being typed in, and the band under the title.',
  font: 'Font',
  fonts: { system: 'System', serif: 'Serif', rounded: 'Rounded' },
  spacing: 'Spacing',
  spacings: { compact: 'Compact', comfortable: 'Comfortable', roomy: 'Roomy' },
  spacingHint: 'Compact suits long office forms; roomy, a short public one.',
  corners: 'Corners',
  cornerKinds: { square: 'Square', soft: 'Soft', round: 'Round' },
  inTheBox: 'In the box',
  labelsHint: 'Where every label sits, unless a group or a field says otherwise.',
  help: 'Help',
  helpWays: { below: 'Under the field', tooltip: 'Behind a (?)', both: 'Both' },
  helpShownHint: 'Where a field’s help shows, unless the field says otherwise: under it, or behind a (?) beside its label — shorter on a busy sheet.',
  readonlyFields: 'Read-only fields',
  readonlyWays: { text: 'As words', box: 'In boxes' },
  readonlyShownHint: 'How a field that cannot be changed shows: its value as plain words, or in its greyed box.',
  quietHints: 'Empty quiet fields',
  quietHintWays: { say: 'Say what to add', nothing: 'Show nothing' },
  quietHintsHint: 'What an empty field in the quiet look shows at rest: “Add story points”, or nothing at all, its label alone saying what it is, as a work item’s Planning does.',
  skin: 'Skin',
  skins: { underline: 'Underline', outlined: 'Outlined', quiet: 'Quiet' },
  skinHint: 'Quiet: each value reads as words until it is pointed at or clicked, as Azure DevOps draws a work item. A group or a field can be quiet on its own.',
  quiet: 'Quiet',
  quietWays: { quiet: 'Quiet', boxed: 'In boxes' },
  quietHint: 'Quiet: values read as words until pointed at or clicked.',
  theme: 'Theme',
  themeNone: 'None',
  themeHint: 'A look in the style of a design system your app already uses. The settings below still change it.',
  colours: 'Colours',
  schemes: { light: 'Light', dark: 'Dark', auto: 'Auto' },
  schemeAutoTitle: 'As the reader’s system has it',
  tokensNote: 'These are the form’s own tokens: every field follows them, and a dark scheme keeps working.',
  style: 'Style',
  styles: { card: 'Card', plain: 'Plain', line: 'Line', framed: 'Framed', inline: 'One line' },
  styleHint: 'Plain draws nothing round it: with no title, its parts simply sit side by side.',
  look: 'Look',
  lookTitle: 'The form’s look: colour, font, spacing',
  formsLook: 'The form’s look',
  close: 'Close',
  // ---- a block
  about: {
    divider: 'A line across the whole row.',
    spacer: 'Empty room: in a grid it keeps a cell empty.',
    image: 'A picture on the page.',
    map: 'A map of the record’s places, in layers: each one set below.',
    related: 'Records of another kind that point at this one — a customer’s invoices — a page at a time, with their count, New and View all.',
    button: 'A button people press.',
    text: 'Words on the page, between the fields.',
    slot: 'A place the app fills with its own part.',
    heading: 'A heading between the fields.',
    form: 'A saved form, drawn as the form draws it. It is changed on its own page; here, which one, its version, its title and where its answers go.',
  },
  delete: 'Delete',
  pictureAddress: 'Picture address',
  pictureShows: 'What the picture shows',
  own: 'Own',
  ownWidth: 'Own width',
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
  sizeS: 'S',
  sizeM: 'M',
  sizeL: 'L',
  full: 'Full',
  fullRow: 'Full row',
  widthInPixels: 'Width in pixels',
  place: 'Place',
  start: 'Start',
  atStart: 'At the start of its row',
  centre: 'Centre',
  end: 'End',
  atEnd: 'At the end of its row',
  link: 'Link',
  caption: 'Caption',
  wordsUnderPicture: 'Words under the picture',
  descriptionHint: 'Read out to those who cannot see it, and shown if the picture does not load; a picture that is a link is named by it. Leave it empty only for a picture that is decoration.',
  opensNewTab: 'Opens in a new tab.',
  readsAs: 'Reads as',
  heading: 'Heading',
  words: 'Words',
  note: 'Note',
  noteTitle: 'Set apart in a soft panel',
  alertBox: 'Alert',
  alertBoxTitle: 'In an alert’s coloured box, as a sheet’s alert; {field} in the words shows its value',
  alertColour: 'Alert colour',
  buttonWords: 'Button words',
  // ---- several picked
  notSet: 'Not set',
  notSetTitle: 'As the group or the page round each says',
  groupThese: 'Group these',
  sideBySide: 'Side by side',
  makeTabs: 'Make tabs',
  apart: 'These sit in different places. Pick parts of one group to group them.',
  together: 'Together',
  eachPicked: (n: number) => `Each of the ${n} picked, in the columns where it sits.`,
  widthNeedsGroup: 'A width needs every part picked in a group with columns.',
  asTheyAre: 'As they are',
  eachAsNow: 'Each as it is now',
  staysRequired: (names: readonly string[]) => (names.length === 1 ? `${names[0]} stays required: the model requires it.` : `${andList(names)} stay required: the model requires them.`),
  // ---- a sheet's header part
  action: 'Action',
  actionHint: 'The name the app receives when it is pressed; the app decides what it does.',
  buttonLooks: { secondary: 'Plain', primary: 'Main', danger: 'Danger', link: 'A link' },
  asksFirst: 'Asks first',
  undoAfter: 'Undo after it',
  undoAfterHint: 'In place of asking first: what it did is said with Undo for a few seconds.',
  hotkey: 'Key, with Alt',
  hotkeyHint: 'A letter or a digit: Alt and it presses the button. Shift+G for Alt+Shift+G.',
  actsAtOnce: 'Nothing: it acts at once',
  numberFrom: 'Number from',
  unitWords: 'Words after the value',
  /** A counter's help: its tooltip, and what a screen reader says of it. */
  counterHelp: 'Help, on pointing at it',
  unitFrom: 'Words after it from a field',
  labelFrom: 'Its words from a field',
  secondFrom: 'Second value from',
  secondWords: 'Second value’s words',
  secondWordsHint: 'Empty: “12 / 21”; with words, one over the other',
  nothing: 'Nothing',
  tone: 'Tone',
  tones: { muted: 'Grey', info: 'Blue', success: 'Green', warning: 'Amber', danger: 'Red' },
  wordsFrom: 'Words from a field',
  wordsFromHint: 'While the field holds words, they show in place of these: a ribbon that says the outcome, or the server’s own warning.',
  tooltip: 'Words on pointing at it',
  canBeClosed: 'Can be closed',
  showValue: 'Show a field’s value in the words',
  valueHint: 'Puts {field} in the words: the record’s value shows there, as the field shows it.',
  buttonsInside: 'Buttons inside',
  addButtonInside: 'Add a button inside',
  buttonInsideWords: 'Button’s words',
  removeNamed: (label: string) => `Remove ${label}`,
  stepsFrom: 'Steps from',
  clickStep: 'People can click a step',
  where: 'Where',
  inHeaderBar: 'In the header bar',
  underTitle: 'Under the title',
  removeStatus: 'Remove the status steps',
  timePerStep: 'Time per step from',
  foldStages: 'Folded stages go under More',
  clickSaves: 'A click saves the record',
  stepsHint: 'The steps are the field’s choices, in their order. Clicking one moves the record to it.',
  /** An app's kind's own settings, by the app's name for its kind. */
  kindSettings: (kind: string) => `${kind} settings`,
  // ---- what the browser fills a box in with, and a phone's keyboard
  filledInAs: 'Filled in by the browser as',
  filledInHint: 'The browser offers what the person saved: their name, email, address. Kept to what the box holds, never more.',
  /** Each purpose the panel offers, by its name in the HTML standard; the kind's own first, and none at all. */
  purposes: {
    '': 'As its kind says',
    off: 'Nothing: the browser offers none',
    name: 'Full name',
    'given-name': 'First name',
    'family-name': 'Last name',
    nickname: 'Nickname',
    email: 'Email',
    tel: 'Phone number',
    organization: 'Company',
    'organization-title': 'Job title',
    'street-address': 'Street address',
    'address-line1': 'Address, first line',
    'address-line2': 'Address, second line',
    'address-level2': 'City',
    'address-level1': 'State or region',
    'postal-code': 'Postcode',
    'country-name': 'Country',
    country: 'Country, by its code',
    bday: 'Birthday',
    sex: 'Gender',
    url: 'Web address',
    language: 'Language',
    username: 'Username',
    'new-password': 'A new password',
    'current-password': 'The password they sign in with',
    'one-time-code': 'A one-time code from a message',
    'cc-name': 'Name on the card',
    'cc-number': 'Card number',
    'cc-exp': 'Card’s expiry date',
    'cc-csc': 'Card’s security code',
  } as Record<string, string>,
  /** An address in parts: whose address it is, before each part's own. */
  addressPurposes: { '': 'Each part as itself', shipping: 'A shipping address', billing: 'A billing address', off: 'Nothing: the browser offers none' } as Record<string, string>,
  keyboard: 'Keyboard on a phone',
  keyboardHint: 'The keys a phone shows as the box is typed in.',
  /** A code scanned with the camera into the field (`options.scan`), and for a link the field of its records holding their codes. */
  scanWithCamera: 'Scan with the camera',
  scanHint: 'A Scan button beside the box reads a barcode, a QR or a DataMatrix code with the camera. A scanner gun types into the box as ever.',
  /** A field never kept in a draft (its `secret`): a PIN, an ID number. */
  secret: 'Never kept in a draft',
  secretHint: 'Its answer goes with the save, but is never kept in a draft — on this device or on your server: a PIN, an ID number.',
  scanCodeField: 'Code kept in',
  scanCodeFieldHint: 'The field of the linked records that holds their code, such as barcode. Left empty, the code is searched as a name.',
  keyboards: { '': 'As its kind says', text: 'Letters', numeric: 'Digits', decimal: 'Digits and a decimal mark', tel: 'A phone’s keypad', email: 'For an email', url: 'For a web address', search: 'For a search' } as Record<string, string>,
  // ---- a public page's bot check
  botCheck: 'Bot check',
  botCheckOn: 'Check that a person sends it',
  botCheckHint: 'Every response says whether it looks sent by a bot; your app decides what to do with it. Nothing is refused on the page.',
  honeypot: 'A hidden box only bots fill',
  minSeconds: 'Fewest seconds to fill it',
  minSecondsHint: 'Sent sooner, it is told as too fast. 0 tells none.',
  captchaIn: 'Captcha drawn in',
  noCaptcha: 'No captcha',
  captchaHint: 'Your app’s captcha (Turnstile, reCAPTCHA, hCaptcha) in one of the page’s own parts: its token goes with the answers.',
  limitsOpens: 'Opens on',
  limitsOpensHint: 'Before then, the form says when it opens in place of its questions. On the page’s time zone.',
  limitsCloses: 'Closes on',
  limitsClosesHint: 'From then, the form says it closed, and takes no more answers: never after someone filled it in.',
  limitsMost: 'Most responses',
  limitsMostHint: 'Counted on your server (the data source’s limits): once it has them all, the form says it is full. Empty: no limit.',
  limitsOne: 'One response per person',
  limitsOneHint: 'Counted on your server for the person signed in: a second is refused before anything is typed.',
  limitsMessage: 'Words while it takes none',
  limitsMessageHint: 'Said under Fieldia’s own: who to ask, when the next one is.',
  continueLater: 'Continue later',
  continueLaterOn: 'Offer Continue later',
  continueLaterHint: 'The answers kept on your server as they are typed, with a link to carry on from any device (the data source’s saveDraft and loadDraft). Hidden where it has none.',
  // ---- a quiz's results
  quizResults: 'Quiz results',
  quizResultsAs: { off: 'Not shown', now: 'Shown as the answers are sent', later: 'Once you have reviewed them' },
  quizResultsHint: 'The person sees their score, each answer marked right or wrong, the right answers and your feedback. Mark the right answers on each question.',
  quizPointsEach: 'Points for a right answer',
  quizPointsEachHint: 'Where a question gives none of its own. 1 unless said.',
  quizShowRight: 'Show the right answer to one missed',
  // ---- a review before sending
  review: 'Review before sending',
  reviewOn: 'Show every answer, with Edit, before it is sent',
  reviewHint: 'The last step’s button reads “Review your answers”: every answer shows by step, each with Edit, then Send.',
  reviewHeading: 'Review’s heading',
  reviewHeadingPlaceholder: 'Check your answers',
  flowOne: 'One question at a time',
  flowOneOn: 'Ask one question at a time',
  flowOneHint: 'Each question on a screen of its own, as Typeform asks: Enter goes on, a choice goes on by itself, ↑ and ↓ move between them.',
  drawRandom: 'Questions in random order',
  drawCount: 'Ask',
  drawAll: 'all',
  drawFixed: 'Keeps its place',
  pagesRandom: 'Pages in random order',
  pagesRandomOn: 'Show the pages in an order drawn for each person',
  pagesRandomHint: 'Each person sees the pages in an order of their own, kept in their draft. A page that keeps its place stays where it is: set it on the page.',
  timeLimit: 'Time limit',
  timeMinutes: 'Minutes to answer',
  timeMinutesHint: 'Said before the questions, with Start; then a countdown. Empty: no limit.',
  timeWarn: 'Warn this many minutes before the end',
  timeThen: 'When the time runs out',
  timeThenAs: { send: 'Send the answers as they are', lock: 'Lock the answers for the person to send' },
  stepTime: 'Minutes for this page',
  noTimeLimit: 'none',
  outbox: 'Without signal',
  outboxOn: 'Keep sends made without signal on the device',
  outboxHint: 'For work where the signal comes and goes — site visits, deliveries: a send that finds no network is kept on the phone, photos and all, and sent when the signal comes back. Never lost on a reload.',
  payment: 'Payment before sending',
  paymentOn: 'Take a payment before the answers go',
  paymentHint: 'A reservation fee, a deposit: Submit shows what is due, and Pay hands it to your app’s own provider (the data source’s pay). Nothing is sent until it is paid. Hidden where your app takes no payments.',
  paymentLabel: 'What the payment is for',
  paymentLabelExample: 'Reservation fee',
  paymentLabelDefault: 'Payment',
  paymentAmount: 'Amount',
  paymentAmountHint: 'A fixed amount, or one of the page’s number or money fields.',
  paymentFrom: 'Amount from',
  paymentFixed: 'This amount',
  paymentCurrency: 'Currency',
  paymentCurrencyHint: 'Its code: EGP, USD, EUR. Empty: a money field’s own.',
  paymentWords: 'Words under the amount',
  prefill: 'Filled from the link',
  prefillHint: 'A link’s parameters fill fields as the page opens — ?utm_source=whatsapp&agent=mona — a campaign’s, a broker’s, a unit’s. A hidden field is sent with the answers and seen by no one; one on the page shows what the link gave. Never over what the person typed.',
  prefillParam: 'The link’s parameter',
  prefillField: 'The field it fills',
  prefillHidden: 'Its own hidden field',
  prefillRemove: 'Take this parameter away',
  prefillAdd: 'Add a parameter',
  // ---- a record page's possible duplicates
  duplicates: 'Possible duplicates',
  duplicatesHint: 'As a record is typed and saved, records with the same value in a field ticked — or a name close to its own — are named under that field, to open instead, let be or merge. Your app’s data source finds them.',
  duplicatesNone: 'No field here can find one: add words, a number, a date, a choice or a link.',
  duplicatesOn: 'Duplicates asked for',
  duplicatesOnAs: { type: 'As they are typed, and as saved', save: 'Only as the record is saved' },
  // ---- a record page's approvals
  approval: 'Approvals',
  approvalOn: 'Its records wait for approval',
  approvalHint: 'Who must approve a record at which level, Approve and Reject for those who may decide, and what was decided — said on the record and in its conversation. Your app’s data source, or its approval engine, says who is asked and in what order. Hidden where it tells none.',
  approvalLock: 'Locked while it waits',
  approvalLockOn: 'Lock the record while it waits',
  approvalLockHint: 'Off: nothing is locked while it waits, and your app’s save refuses what it must.',
  approvalOpen: 'Open while it waits',
  approvalOpenHint: 'The fields ticked stay open while the record waits for a decision; every other one is locked, its lock saying why.',
  // ---- AI help's Fill from text
  suggest: 'Fill from text (AI)',
  suggestOn: 'Offer Fill from text',
  suggestHint: 'A button that fills the form from words people paste — an e-mail, a signature, a message — or files, by your app’s own AI (the data source’s suggest). Each value is shown on its field to accept or reject, with Undo. Hidden where the source has none.',
  suggestLabel: 'Its button’s words',
  suggestLabelExample: 'Fill from an e-mail',
  suggestFrom: 'Fills from',
  suggestFromHint: 'Words people paste or files they add, or the words a field of the record holds — a buyer’s message, a visit’s notes — read at a press.',
  suggestPasted: 'Words pasted, or files',
  suggestFields: 'Fields it may fill',
  suggestFieldsHint: 'None ticked: every field shown that people can change.',
  // ---- what the screen's records are called
  recordsCalled: 'Records are called',
  recordsOne: 'One record is called',
  recordsOneExample: 'customer',
  recordsHint: 'Fieldia’s own sentences name this screen’s records so: “A customer with this phone already exists”, “17 customers imported”, “New customer”. Left empty, they say “record”.',
  recordsOther: 'More are called',
  recordsOtherExample: 'customers',
  recordsBoth: 'Give the word for one and for more.',
  recordsA: 'One with its article',
  recordsAExample: 'a customer',
  recordsAHint: '“A customer”, “an invoice”: left empty, a sentence that needs it says “A record”.',
  recordsLowerLabels: 'Field names small inside them',
  templatesOn: 'Templates people save',
  templatesOnHint: 'A Templates menu over the record: Save as template keeps what a record holds by a name (“Botox package”), and a new record starts from one. Kept by your app’s data source, or in the person’s browser.',
  recordsLowerLabelsHint: 'Inside every sentence Fieldia says: “A customer with this phone already exists”, “Choose at least 2 for interests”. A label that begins a sentence, and a name like IBAN or WhatsApp, stay as written.',
};
